import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const DateSelector = ({ selectedDateType, selectedDate, onDateChange, error }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date()?.getFullYear());

  const dateTypes = [
    { id: 'specific', name: 'Specific Date', icon: 'Calendar', description: 'I have exact travel dates' },
    { id: 'month', name: 'Specific Month', icon: 'CalendarDays', description: 'I want to travel in a particular month' },
    { id: 'season', name: 'Seasonal', icon: 'Sun', description: 'I\'m flexible with seasons' }
  ];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const seasons = [
    { id: 'spring', name: 'Spring', months: 'Mar - May', icon: 'Flower2' },
    { id: 'summer', name: 'Summer', months: 'Jun - Aug', icon: 'Sun' },
    { id: 'autumn', name: 'Autumn', months: 'Sep - Nov', icon: 'Leaf' },
    { id: 'winter', name: 'Winter', months: 'Dec - Feb', icon: 'Snowflake' }
  ];

  const handleDateTypeChange = (type) => {
    onDateChange({ type, value: null });
  };

  const handleSpecificDateChange = (field, value) => {
    const currentDate = selectedDate?.value || { startDate: '', endDate: '' };
    onDateChange({
      type: 'specific',
      value: { ...currentDate, [field]: value }
    });
  };

  const handleMonthChange = (monthName) => {
    const monthIndex = months?.indexOf(monthName) + 1; // 1-based month
    onDateChange({ 
      type: 'month', 
      value: {
        month: monthIndex,
        year: selectedYear,
        monthName: monthName
      }
    });
  };

  const handleSeasonChange = (seasonId) => {
    onDateChange({
      type: 'season',
      value: seasonId
    });
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    // If month already selected, update with new year
    if (selectedDate?.value?.month) {
      onDateChange({
        type: 'month',
        value: {
          ...selectedDate?.value,
          year: year
        }
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getCurrentYear = () => new Date()?.getFullYear();
  const getNextYear = () => getCurrentYear() + 1;

  const getYearOptions = () => {
    const currentYear = getCurrentYear();
    return [currentYear, currentYear + 1, currentYear + 2];
  };

  return (
    <div className="space-y-6">
      {/* Date Type Selection */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">When do you want to travel?</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {dateTypes?.map((type) => (
            <button
              key={type?.id}
              onClick={() => handleDateTypeChange(type?.id)}
              className={`p-4 rounded-lg border-2 text-left transition-all hover:scale-105 ${
                selectedDateType === type?.id
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-border hover:border-primary/50 hover:bg-muted/50'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`p-2 rounded-lg ${
                  selectedDateType === type?.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  <Icon name={type?.icon} size={20} />
                </div>
                <div className="flex-1">
                  <h4 className={`font-medium ${
                    selectedDateType === type?.id ? 'text-primary' : 'text-foreground'
                  }`}>
                    {type?.name}
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    {type?.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Specific Date Selection */}
      {selectedDateType === 'specific' && (
        <div className="bg-muted/30 p-6 rounded-lg">
          <h4 className="font-medium text-foreground mb-4">Select your travel dates</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Departure Date
              </label>
              <input
                type="date"
                value={selectedDate?.value?.startDate || ''}
                onChange={(e) => handleSpecificDateChange('startDate', e?.target?.value)}
                min={new Date()?.toISOString()?.split('T')?.[0]}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Return Date
              </label>
              <input
                type="date"
                value={selectedDate?.value?.endDate || ''}
                onChange={(e) => handleSpecificDateChange('endDate', e?.target?.value)}
                min={selectedDate?.value?.startDate || new Date()?.toISOString()?.split('T')?.[0]}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          {selectedDate?.value?.startDate && selectedDate?.value?.endDate && (
            <div className="mt-4 p-3 bg-primary/10 rounded-lg">
              <div className="flex items-center space-x-2 text-primary">
                <Icon name="Calendar" size={16} />
                <span className="font-medium">
                  {formatDate(selectedDate?.value?.startDate)} - {formatDate(selectedDate?.value?.endDate)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Month Selection */}
      {selectedDateType === 'month' && (
        <div className="bg-muted/30 p-6 rounded-lg">
          <h4 className="font-medium text-foreground mb-4">Select preferred month and year</h4>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Travel Year *
            </label>
            <div className="flex gap-2">
              {getYearOptions()?.map((year) => (
                <button
                  key={year}
                  onClick={() => handleYearChange(year)}
                  className={`px-4 py-2 rounded-lg border text-center transition-all hover:scale-105 ${
                    selectedYear === year
                      ? 'border-primary bg-primary text-primary-foreground shadow-md'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {months?.map((month, index) => (
              <button
                key={month}
                onClick={() => handleMonthChange(month)}
                className={`p-3 rounded-lg border text-center transition-all hover:scale-105 ${
                  selectedDate?.value?.monthName === month
                    ? 'border-primary bg-primary text-primary-foreground shadow-md'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
              >
                <div className="font-medium">{month}</div>
              </button>
            ))}
          </div>

          {selectedDate?.value?.monthName && selectedDate?.value?.year && (
            <div className="mt-4 p-3 bg-primary/10 rounded-lg">
              <div className="flex items-center space-x-2 text-primary">
                <Icon name="Calendar" size={16} />
                <span className="font-medium">
                  Any day in {selectedDate?.value?.monthName} {selectedDate?.value?.year}
                </span>
              </div>
            </div>
          )}

          {selectedDate?.value?.monthName && !selectedDate?.value?.year && (
            <div className="mt-4 p-3 bg-error/10 rounded-lg border border-error/20">
              <div className="flex items-center space-x-2 text-error">
                <Icon name="AlertCircle" size={16} />
                <span className="text-sm">Please select a year for your travel month</span>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Season Selection */}
      {selectedDateType === 'season' && (
        <div className="bg-muted/30 p-6 rounded-lg">
          <h4 className="font-medium text-foreground mb-4">Select preferred season</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {seasons?.map((season) => (
              <button
                key={season?.id}
                onClick={() => handleSeasonChange(season?.id)}
                className={`p-4 rounded-lg border-2 text-center transition-all hover:scale-105 ${
                  selectedDate?.value === season?.id
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
              >
                <div className={`p-3 rounded-lg mx-auto mb-2 w-fit ${
                  selectedDate?.value === season?.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  <Icon name={season?.icon} size={24} />
                </div>
                <h5 className={`font-medium ${
                  selectedDate?.value === season?.id ? 'text-primary' : 'text-foreground'
                }`}>
                  {season?.name}
                </h5>
                <p className="text-xs text-muted-foreground mt-1">
                  {season?.months}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
      {error && (
        <p className="text-error text-sm">{error}</p>
      )}
    </div>
  );
};

export default DateSelector;